import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GurujiChatWidget from '../components/GurujiChatWidget';
import DownloadGate from '../components/DownloadGate';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, Video, Youtube, Download, 
  PlayCircle, BookOpen, GraduationCap, Loader2, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Resources = () => {
  const [materials, setMaterials] = useState([]);
  const [videos, setVideos] = useState([]);
  const [youtubeLinks, setYoutubeLinks] = useState([]);
  const [currentAffairs, setCurrentAffairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDownloadGate, setShowDownloadGate] = useState(false);
  const [downloadInfo, setDownloadInfo] = useState({ url: '', fileName: '', type: '' });

  useEffect(() => {
    fetchAllResources();
  }, []);

  const fetchAllResources = async () => {
    try {
      const [materialsRes, videosRes, youtubeRes, blogRes] = await Promise.all([
        fetch(`${API_URL}/api/materials`),
        fetch(`${API_URL}/api/videos`),
        fetch(`${API_URL}/api/youtube`),
        fetch(`${API_URL}/api/blog?category=Current Affairs`)
      ]);

      const materialsData = await materialsRes.json();
      const videosData = await videosRes.json();
      const youtubeData = await youtubeRes.json();
      const blogData = await blogRes.json();

      setMaterials(materialsData);
      setVideos(videosData);
      setYoutubeLinks(youtubeData);
      setCurrentAffairs(blogData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setLoading(false);
    }
  };

  const handleDownload = (materialId, fileName) => {
    setDownloadInfo({
      url: `${API_URL}/api/materials/download/${materialId}`,
      fileName: fileName,
      type: 'material'
    });
    setShowDownloadGate(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Page Header */}
      <section className="bg-gradient-to-b from-red-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_vidya365-coaching/artifacts/051wobd3_IMG-20260204-WA0032.jpg" 
              alt="Vidya365" 
              className="h-20 w-auto mx-auto mb-6 shadow-lg rounded-md"
            />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Study Resources</h1>
            <p className="text-xl text-gray-600 mb-4">
              Access comprehensive course materials, recorded lectures, and YouTube videos
            </p>
            <div className="flex items-center justify-center space-x-6 text-gray-600">
              <div className="flex items-center">
                <BookOpen className="mr-2 text-red-600" size={20} />
                <span>{materials.length}+ Materials</span>
              </div>
              <div className="flex items-center">
                <Video className="mr-2 text-red-600" size={20} />
                <span>{videos.length}+ Videos</span>
              </div>
              <div className="flex items-center">
                <GraduationCap className="mr-2 text-red-600" size={20} />
                <span>Free Access</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="materials" className="max-w-6xl mx-auto">
            <TabsList className="grid w-full grid-cols-4 mb-8">
              <TabsTrigger value="materials" className="text-lg">
                <FileText className="mr-2" size={20} />
                Course Materials
              </TabsTrigger>
              <TabsTrigger value="current-affairs" className="text-lg">
                <BookOpen className="mr-2" size={20} />
                Current Affairs
              </TabsTrigger>
              <TabsTrigger value="recorded" className="text-lg">
                <Video className="mr-2" size={20} />
                Recorded Videos
              </TabsTrigger>
              <TabsTrigger value="youtube" className="text-lg">
                <Youtube className="mr-2" size={20} />
                YouTube Videos
              </TabsTrigger>
            </TabsList>

            {/* Course Materials Tab */}
            <TabsContent value="materials" className="space-y-6">
              <Card className="bg-gradient-to-br from-red-50 to-white border-2 border-red-100">
                <CardHeader>
                  <CardTitle className="flex items-center text-red-900">
                    <FileText className="mr-2" size={24} />
                    Study Materials
                  </CardTitle>
                  <CardDescription>Download PDFs, notes, and practice materials</CardDescription>
                </CardHeader>
              </Card>

              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="animate-spin mx-auto text-red-600" size={48} />
                  <p className="text-gray-600 mt-4">Loading materials...</p>
                </div>
              ) : materials.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <FileText className="mx-auto text-gray-400 mb-4" size={48} />
                    <p className="text-gray-600">No materials available yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {materials.map((material) => (
                    <Card key={material.id} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            <FileText className="text-red-600 mt-1" size={24} />
                            <div>
                              <CardTitle className="text-lg">{material.title}</CardTitle>
                              <CardDescription className="mt-1">{material.description}</CardDescription>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div className="space-y-1 text-sm text-gray-600">
                            <p className="font-medium text-red-600">{material.course_category}</p>
                            <p>File: {material.file_name}</p>
                            <p>Uploaded: {new Date(material.created_at).toLocaleDateString()}</p>
                          </div>
                          <button
                            onClick={() => handleDownload(material.id, material.file_name)}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center"
                          >
                            <Download size={16} className="mr-2" />
                            Download
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Current Affairs Tab */}
            <TabsContent value="current-affairs" className="space-y-6">
              <Card className="bg-gradient-to-br from-red-50 to-white border-2 border-red-100">
                <CardHeader>
                  <CardTitle className="flex items-center text-red-900">
                    <BookOpen className="mr-2" size={24} />
                    Current Affairs
                  </CardTitle>
                  <CardDescription>Stay updated with latest current affairs for competitive exams</CardDescription>
                </CardHeader>
              </Card>

              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="animate-spin mx-auto text-red-600" size={48} />
                  <p className="text-gray-600 mt-4">Loading current affairs...</p>
                </div>
              ) : currentAffairs.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
                    <p className="text-gray-600">No current affairs posts available yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {currentAffairs.map((post) => (
                    <Card key={post.id} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-start space-x-3">
                          <BookOpen className="text-red-600 mt-1 flex-shrink-0" size={24} />
                          <div className="flex-1">
                            <CardTitle className="text-lg mb-2">{post.title}</CardTitle>
                            <CardDescription className="line-clamp-2">{post.excerpt}</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-gray-600 space-y-1">
                            <p>{new Date(post.created_at).toLocaleDateString()}</p>
                            <p className="text-red-600 font-medium">{post.read_time}</p>
                          </div>
                          <div className="flex gap-2">
                            <a
                              href={`/latest-updates/${post.id}`}
                              className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
                            >
                              Read More
                              <ExternalLink className="ml-2" size={14} />
                            </a>
                            {post.pdf_url && (
                              <button
                                onClick={() => {
                                  setDownloadInfo({
                                    url: `${API_URL}/api/blog/pdf/${post.id}`,
                                    fileName: `${post.title}.pdf`,
                                    type: 'pdf'
                                  });
                                  setShowDownloadGate(true);
                                }}
                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                              >
                                <Download className="mr-2" size={14} />
                                PDF
                              </button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Recorded Videos Tab */}
            <TabsContent value="recorded" className="space-y-6">
              <Card className="bg-gradient-to-br from-red-50 to-white border-2 border-red-100">
                <CardHeader>
                  <CardTitle className="flex items-center text-red-900">
                    <Video className="mr-2" size={24} />
                    Recorded Lectures
                  </CardTitle>
                  <CardDescription>Watch recorded video lectures by expert faculty</CardDescription>
                </CardHeader>
              </Card>

              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="animate-spin mx-auto text-red-600" size={48} />
                  <p className="text-gray-600 mt-4">Loading videos...</p>
                </div>
              ) : videos.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <Video className="mx-auto text-gray-400 mb-4" size={48} />
                    <p className="text-gray-600">No recorded videos available yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {videos.map((video) => (
                    <Card key={video.id} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            <PlayCircle className="text-red-600 mt-1" size={24} />
                            <div>
                              <CardTitle className="text-lg">{video.title}</CardTitle>
                              <CardDescription className="mt-1">{video.description}</CardDescription>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div className="space-y-1 text-sm text-gray-600">
                            <p className="font-medium text-red-600">{video.course_category}</p>
                            <p>File: {video.file_name}</p>
                            <p>Uploaded: {new Date(video.created_at).toLocaleDateString()}</p>
                          </div>
                          <a
                            href={`${API_URL}/api/videos/stream/${video.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center"
                          >
                            <PlayCircle size={16} className="mr-2" />
                            Watch
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* YouTube Videos Tab */}
            <TabsContent value="youtube" className="space-y-6">
              <Card className="bg-gradient-to-br from-red-50 to-white border-2 border-red-100">
                <CardHeader>
                  <CardTitle className="flex items-center text-red-900">
                    <Youtube className="mr-2" size={24} />
                    YouTube Video Library
                  </CardTitle>
                  <CardDescription>Curated YouTube videos for exam preparation</CardDescription>
                </CardHeader>
              </Card>

              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="animate-spin mx-auto text-red-600" size={48} />
                  <p className="text-gray-600 mt-4">Loading videos...</p>
                </div>
              ) : youtubeLinks.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <Youtube className="mx-auto text-gray-400 mb-4" size={48} />
                    <p className="text-gray-600">No YouTube links available yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {youtubeLinks.map((link) => (
                    <Card key={link.id} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-start space-x-3">
                          <Youtube className="text-red-600 mt-1" size={24} />
                          <div>
                            <CardTitle className="text-lg">{link.title}</CardTitle>
                            <CardDescription className="mt-1">{link.course_category}</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-gray-600">
                            Added: {new Date(link.created_at).toLocaleDateString()}
                          </p>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center"
                          >
                            <Youtube size={16} className="mr-2" />
                            Watch on YouTube
                            <ExternalLink size={14} className="ml-2" />
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
      
      {/* Download Gate Modal */}
      <DownloadGate
        isOpen={showDownloadGate}
        onClose={() => setShowDownloadGate(false)}
        downloadUrl={downloadInfo.url}
        fileName={downloadInfo.fileName}
        resourceType={downloadInfo.type}
      />
      
      {/* Floating Guruji AI Chat Widget */}
      <GurujiChatWidget />
    </div>
  );
};

export default Resources;
